import React, { useCallback, useContext, useEffect } from 'react';
import _ from 'lodash';
import ModalCtrl from 'sg-modal-ctrl';
import { Link, ModalNextProvider } from '@jutro/components';
import { SgActiveTestsContext, SgBreakdownUtil } from 'saga-modules';
import { TranslatorContext } from '@jutro/locale';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import SpmConstants from 'sg-spm-constants';
import CommonConstants from 'sg-common-constants';
import SgSpmBreakdownTable from 'sg-spm-breakdown-table';
import metadata from './SgCEPBreakdownCoverLevelSelection.metadata.json5';
import messages from './SgCEPBreakdownCoverLevelSelection.messages';

function SgCEPBreakdownAddonContent(props) {
    const {
        id,
        modelVM,
        updateData,
        showErrors,
        addon,
        contentMessages,
        path,
        onValidateParent,
        updateCoveragesAndCalculate
    } = props;
    const {
        isComponentValid,
        onValidate
    } = useValidation(id);
    const translator = useContext(TranslatorContext);
    const { activeMvtTests } = useContext(SgActiveTestsContext);

    const SBACoverages = SpmConstants.SBA_COVERAGES;
    const CarBreakdownServices = SpmConstants.CAR_BREAKDOWN_SERVICES;
    const CoverStartDate = !_.isUndefined(modelVM.baseData.effectiveDate) ? modelVM.baseData.effectiveDate.value
        : modelVM.baseData.periodStartDate.value;

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    const addonIndex = modelVM.lobData.privateMotor.offerings.children[0].coverages.vehicleCoverages.children[0].coverages.value
        .findIndex((el) => el.patternCode === SpmConstants.BREAKDOWN_CODE);
    const addonTerm = addon.terms.find((term) => term.patternCode === SpmConstants.BREAKDOWN_LEVEL_CODE);

    const coverLevelOptions = _.sortBy(addonTerm.options, (option) => option.amount.amount);

    useEffect(() => {
        if ((activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_4177) || activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_1169))
            && isComponentValid && updateCoveragesAndCalculate && addon.selected && addonTerm && addonTerm.updated && addonTerm.chosenTerm) {
            updateCoveragesAndCalculate();
        }
    }, [activeMvtTests, addon.selected, addonTerm, isComponentValid, updateCoveragesAndCalculate]);

    const updateBreakdown = useCallback((value) => {
        const chosenOption = addonTerm.options.find(((option) => option.code === value));
        addon.amount.amount = chosenOption ? chosenOption.amount.amount : addon.amount.originalAmount_Ext;
        addon.amount.annualAmount_Ext = chosenOption ? chosenOption.amount.annualAmount_Ext : addon.amount.annualAmount_Ext;
        addonTerm.updated = true;
        addonTerm.chosenTerm = value;
        updateData(modelVM);
    }, [addon, addonTerm, modelVM, updateData]);

    useEffect(() => {
        if (addon.selected && _.isNil(addonTerm.chosenTerm)) {
            document.getElementById('breakdownCoverLevelSelection').focus();
            onValidateParent(false, id);
        } else if (!addon.selected) {
            updateBreakdown(undefined);
        }
    }, [addon.selected]);

    const componentProps = {
        SBACoverages: SgBreakdownUtil.SBACoveragesPriceOverride(CoverStartDate, SBACoverages, true, addon),
        Services: CarBreakdownServices
    };

    const getModalJsx = () => {
        return (
            <>
                <SgSpmBreakdownTable hideButtons hideTooltip {... componentProps} />
                <div className="sgBreakdownAddonTableSubText">{translator(messages.whenBreakdownOccurs)}</div>
            </>
        );
    };

    const showMoreInfoModal = useCallback((event) => {
        if (event) {
            event.preventDefault();
        }
        const modalProps = {
            title: translator(contentMessages.Name),
            closeBtnLabel: translator(messages.Close),
            modalJsx: getModalJsx(),
            titleVisibility: true
        };

        ModalNextProvider.showModal(
            <ModalCtrl {...modalProps} />
        );
    }, [ModalNextProvider]);

    const overrideProps = {
        breakdownCoverLevelSelection: {
            availableValues: coverLevelOptions,
            path: `${path}[${addonIndex}]`,
            onValueChange: updateBreakdown,
            value: addonTerm.options.find((option) => option.code === addonTerm.chosenTerm),
            required: addon.selected,
            label: translator(messages.coverLevelLabel),
            requiredFieldValidationMessage: translator(messages.validationMessages),
        }
    };

    return (
        <>
            <div className={`sg-add-on-icon-container ${addon.selected ? 'sg-addon-selected' : ''}`}>
                <div
                    className={`sg-add-on-icon-box sg-add-on-icon_${addon.patternCode}`}
                    aria-hidden="true"
                />
                <div className="sg-addon-icon-added">&nbsp;</div>
            </div>
            <div id={`coverageHeader_${addon.patternCode}`} className="sg-add-on-name">
                {translator(contentMessages.Name)}
            </div>
            {addon.selected && (
                <ViewModelForm
                    uiProps={metadata.componentContent}
                    model={modelVM}
                    showErrors={showErrors}
                    overrideProps={overrideProps}
                    onValidationChange={onValidate}
                />
            )}
            <div className="sg-add-on-description-box">
                {translator(contentMessages.Description)}
            </div>
            <Link
                href="/"
                id="showMoreVehicleUseHelpText"
                onClick={showMoreInfoModal}
            >
                {translator(messages.ViewOurCoverOptions)}
            </Link>
        </>
    );
}

export default SgCEPBreakdownAddonContent;
